<template>
  <div class="mb-3 space-y-2">
    <label class="block text-left text-sm">{{ label }} — chọn file mới hoặc module đã lưu</label>
    <select
      :value="modelValue"
      :aria-label="`${label} đã tải lên`"
      class="select select-bordered select-sm w-full"
      :disabled="loading"
      @change="emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
    >
      <option value="">{{ loading ? "Đang tải module…" : "Chọn module đã tải lên" }}</option>
      <option v-for="module in modules" :key="module.id" :value="module.id">
        {{ module.file_name }}
      </option>
    </select>
    <p v-if="!loading && !modules.length" class="text-xs text-base-content/60">Chưa có module phù hợp trong thư viện.</p>
    <p v-if="loadError" class="text-error text-xs">Không tải được danh sách. <button class="link" @click="load">Thử lại</button></p>
    <div class="flex gap-2">
      <input ref="fileInput" type="file" :accept="accept" class="file-input file-input-bordered file-input-sm min-w-0 grow" @change="selectFile" />
      <button class="btn btn-outline btn-sm" :disabled="!file || uploading" @click="upload">
        <span v-if="uploading" class="loading loading-spinner loading-xs"></span>
        <span v-else>Tải lên</span>
      </button>
    </div>
    <p v-if="file" class="text-xs text-base-content/60">{{ file.name }}</p>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import { listFindDMLModules, uploadFindDMLModule, type FindDMLModule, type FindDMLModuleType } from "@/api/findDml";

const props = defineProps<{
  agentId: string;
  type: FindDMLModuleType;
  accept: string;
  label: string;
  modelValue: string;
  autoSelectLatest?: boolean;
}>();
const emit = defineEmits<{ "update:modelValue": [value: string] }>();
const modules = ref<FindDMLModule[]>([]);
const loading = ref(false);
const loadError = ref(false);
const uploading = ref(false);
const file = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

const load = async () => {
  loading.value = true;
  loadError.value = false;
  modules.value = [];
  try {
    modules.value = await listFindDMLModules(props.type);
    if (props.autoSelectLatest && !props.modelValue && modules.value.length) {
      emit("update:modelValue", modules.value[0].id);
    }
  } catch {
    loadError.value = true;
  } finally {
    loading.value = false;
  }
};

const selectFile = (event: Event) => {
  file.value = (event.target as HTMLInputElement).files?.[0] ?? null;
};

const upload = async () => {
  if (!file.value || !props.agentId) return;
  uploading.value = true;
  try {
    const saved = await uploadFindDMLModule({ agentId: props.agentId, type: props.type, file: file.value });
    modules.value = [saved, ...modules.value];
    emit("update:modelValue", saved.id);
    file.value = null;
    if (fileInput.value) fileInput.value.value = "";
    toast.success("Đã lưu module vào thư viện dùng chung.");
  } catch {
    toast.error("Không thể tải module lên thư viện.");
  } finally {
    uploading.value = false;
  }
};

watch(() => props.type, () => {
  file.value = null;
  emit("update:modelValue", "");
  void load();
});
onMounted(load);
</script>
